import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IMAGES, kiosk_IMAGES } from "@/assets/IMAGES";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import CustomAlertDialog from "@/components/custom-component/CustomAlertDialog";
import {
  TicketResponse,
  useGenerateTicketForServiceMutation,
  useGetAllDepartmentServiceByIdQuery,
} from "@/services/SelfServiceKiosk";
import { toast } from "@/components/ui/UseToast";
import axios from "axios";
const { VITE_VIEW_S3_URL } = import.meta.env;
import { default as defaultImg } from "@/assets/default.jpg";
import { Department } from "@/app/types/department";
import { capitalizeFirstLetter } from "@/utils/Helper";
import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
const { VITE_PRINTER_URL } = import.meta.env;
import { useLazyGetFormVersionQuery } from "@/services/FormBuilderApi";
import "@/styles/print.css"; // Import print styles
interface Schedule {
  day: string;
  opening_time: string;
  closing_time: string;
  ticket_end_time: string;
}

interface Service {
  id: number;
  name: string;
  is_multi_step: boolean;
  schedule: Schedule[];
  logo?: string;
  form_slug?: string;
  // Remove img property to match ServiceofDepartment structure
}

const initialValue = {
  limit: 8,
  offset: 0
}

const getCurrentDateTime = () => {
  const now = new Date();
  const time = now.toLocaleTimeString("en-GB", { hour12: true }); // 24-hour format
  const date = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return { time, date };
};

const OfferService: React.FC = () => {
  const navigate = useNavigate();
  const { time, date } = getCurrentDateTime();
  const location = useLocation();
  const department: Department = location.state?.department; // Retrieve passed data

  const [getFormVersion] = useLazyGetFormVersionQuery();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Service | null>(null); // Selected card data
  const itemsPerPage = 8;
  const [isGeneratingTicket, setIsGeneratingTicket] = useState<boolean>(false);

  const { searchCriteria, query, setSearchCriteria } = useTableData(initialValue);

  const { data: servicesData, isLoading, refetch } =
    useGetAllDepartmentServiceByIdQuery({ department_id: department.id as number, query: query });

  const totalRecords = servicesData?.total_count ?? 0;


  const {
    handleNextPage,
    handlePrevPage,
    isLastPage
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });


  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = servicesData?.data?.slice(startIndex, endIndex) || [];
  const [ticketResponse, SetTicketResponse] = useState<TicketResponse>();
  const [generateTicket] = useGenerateTicketForServiceMutation();



  const handleCardClick = (item: Service) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${date.toLocaleDateString()} ${formattedHours}:${formattedMinutes} ${period}`;
  };

  const handleBrowserPrint = (ticketContent: string) => {
    // Create a temporary div for printing
    const printDiv = document.createElement('div');
    printDiv.id = 'print-content';

    // Apply direct styles
    Object.assign(printDiv.style, {
      fontFamily: 'Arial, sans-serif',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: 'white',
      width: '80mm',
      margin: '0 auto'
    });

    // Parse and format the content properly
    const lines = ticketContent.trim().split('\n').map(line => line.trim());
    const formattedContent = lines.map(line => {
      // Special handling for Service Closing Time
      if (line.includes('Service Closing Time:')) {
        // The closing time already includes AM/PM from GenerateToken
        const timeValue = line.split('Service Closing Time:')[1].trim();
        return `
          <div style="margin: 8px 0; line-height: 1.5;">
            <span style="font-weight: bold;">Service Closing Time:</span>
            <span style="font-size: 1.1em;">${timeValue}</span>
          </div>
        `;
      }

      // Handle created time (date/time stamp)
      if (line.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        return `<div style="margin: 8px 0; font-size: 1.1em;">${line}</div>`;
      }

      // Handle other lines with colons
      if (line.includes(':')) {
        const [label, value] = line.split(':').map(str => str.trim());
        return `
          <div style="margin: 8px 0; line-height: 1.5;">
            <span style="font-weight: bold;">${label}:</span>
            <span style="font-size: 1.1em;">${value}</span>
          </div>
        `;
      }

      // Handle other lines
      return `<div style="margin: 8px 0; font-size: 1.1em;">${line}</div>`;
    }).join('');

    printDiv.innerHTML = `
      <div style="border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px;">
        <h2 style="font-size: 1.2em; margin: 0;">Queue Ticket</h2>
      </div>
      ${formattedContent}
      <div style="border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px;">
        <small style="font-size: 0.8em;">Thank you for your patience</small>
      </div>
    `;

    // Remove any existing print content
    const existingPrintContent = document.getElementById('print-content');
    if (existingPrintContent) {
      existingPrintContent.remove();
    }

    document.body.appendChild(printDiv);

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.removeChild(printDiv);
      }, 100);
    }, 100);
  };

  const GenerateToken = async (serviceId: number, form_slug: string | undefined) => {
    // if (form_slug) {
    //   try {
    //     await getFormVersion(form_slug).unwrap();
    //     window.location.href = `/form/${form_slug}?service=${serviceId}`;
    //   } catch (error) { }
    // }

    if (isGeneratingTicket) {
      toast({
        variant: "destructive",
        title: "Please wait",
        description: "A ticket is currently being generated",
      });
      return;
    }

    setIsGeneratingTicket(true);
    try {
      await generateTicket(serviceId)
        .unwrap()
        .then(async (res) => {
          SetTicketResponse(res);
          setIsModalOpen(true);

          const closingTimeParts = res?.closing_time.split(":").map(Number);
          let formattedMessage;
          if (closingTimeParts.length >= 2) {
            const [hour, minute] = closingTimeParts;
            let closingTimeAdjusted = hour % 12 || 12;
            const period = hour >= 12 ? "PM" : "AM";
            const formattedClosingTime = `${closingTimeAdjusted}:${String(minute).padStart(2, "0")} ${period}`;
            const formattedCreatedTime = new Date().toLocaleString();

            formattedMessage = `
                Service Name: ${capitalizeFirstLetter(res.service_name)}
                Ticket Number: ${res.assigned_number}
                Counter Code: ${res.counter_code || res.counter_id}
                Service Closing Time: ${formattedClosingTime}
                ${formattedCreatedTime}
            `;

            try {
              await axios.post(`${VITE_PRINTER_URL}`, {
                message: formattedMessage,
              });

              toast({
                title: "Success",
                description: "Ticket sent to the printer.",
              });
            } catch (printError) {
              console.error("Error sending to printer:", printError);
              // Fallback to browser printing
              handleBrowserPrint(formattedMessage);
              toast({
                title: "Using browser print",
                description: "Printer not connected. Using browser print instead.",
              });
            }
          }
        })
        .catch((err) => {
          toast({
            variant: "destructive",
            title: `Failed to create Token.`,
            description:
              err.data.message || "Ticket generation failed. Please try again or contact staff for assistance.",
          });
        })
        .finally(() => {
          setIsGeneratingTicket(false);
        });
    } catch (error: any) {
      setIsGeneratingTicket(false);
      const message =
        error?.data?.detail || error?.data?.message || "Something went wrong";
      toast({
        variant: "destructive",
        title: "Error Generating Token",
        description: message,
      });
    }
  };

  const renderSkeleton = () => {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <Card
        key={Number(index)}
        className="animate-pulse flex flex-col items-center justify-center w-full py-6 bg-[#EDEDED]"
      >
        <CardContent className="flex flex-col items-center justify-center text-center">
          <div className="w-[5rem] h-[5rem] bg-gray-300 rounded-full" />
        </CardContent>
        <CardFooter className="flex items-center justify-center">
          <div className="w-[8rem] h-[1rem] bg-gray-300 rounded-md" />
        </CardFooter>
      </Card>
    ));
  };

  const renderBody = () => {
    const closingTimeParts = ticketResponse?.closing_time
      ?.split(":")
      .map(Number) || [0, 0]; // Extract hour and minute, default to [0, 0]
    const [closingHour, closingMinute] = closingTimeParts; // Extract hour and minute

    const closingTimeAdjusted = closingHour % 12 || 12; // Convert to 12-hour format, handling 12 AM/PM case
    const period = closingHour >= 12 ? "PM" : "AM"; // Determine AM/PM

    const formattedClosingTime = `${closingTimeAdjusted}:${String(closingMinute).padStart(2, "0")} ${period}`;


    return (
      <div className="text-center p-4 bg-white h-[14rem] relative">
        {/* Service Name */}
        <h2 className="capitalize text-[1.4rem] text-gray-600 font-semibold">
          {ticketResponse?.service_name}
        </h2>

        {/* Service Code */}
        <p className="text-black text-[1.8rem] font-bold">
          {ticketResponse?.assigned_number}
        </p>

        {/* Information */}
        <p className="text-gray-600 text-nowrap">
          <div className="flex items-center justify-center gap-2">
            Counter No <br />
            <span className="text-black text-[1rem] font-bold">
              {ticketResponse?.counter_id}
            </span>
          </div>
        </p>

        {/* Closing Time (positioned at the bottom center) */}
        <div className="absolute bottom-0 left-1/2 my-4 transform -translate-x-1/2 w-full">
          <p className="text-[0.8rem] text-gray-600 font-normal">
            Service Closing Time
          </p>
          <p className="text-[1.1rem] text-black font-semibold">
            {formattedClosingTime ?? ticketResponse?.closing_time}
          </p>
        </div>
      </div>
    );
  };

  const renderNoServiceMessage = () => (
    <div className="text-center py-6 flex justify-center items-center w-full">
      <h2 className="text-gray-600 text-[1.2rem] font-semibold">
        No Services available
      </h2>
    </div>
  );



  return (
    <main className="w-full flex flex-col items-center justify-center p-0 md:p-1 bg-gray-50">
      <div className="m-0 md:m-3 w-[80%]">
        {/* Header */}

        <div className="flex py-3 md:py-0 flex-col items-center justify-center md:flex-row md:justify-between">
          <div className="flex items-center">
            <img
              src={IMAGES.logo_icon}
              alt="logo"
              className="w-[5rem] h-[5rem] object-contain"
            />
            <p className="text-primary text-[1.4rem] font-bold">QMS</p>
          </div>
          <h4 className="capitalize my-2 md:my-0 text-[1.5rem] md:text-[2rem] text-primary">
            {department.name}
          </h4>
        </div>
        <Button
          onClick={() => {
            navigate("/kiosk-home-page");
          }}
          variant={"ghost"}
          className="w-16 mt-2"
        >
          {" "}
          <ChevronLeftIcon className="h-6 w-6" />
        </Button>
        {/* Services Section */}
        <section className="mx-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-0 md:mt-8">
          {isLoading ? (
            renderSkeleton()
          ) : currentServices.length > 0 ? (
            currentServices.map((item: Service, index: number) => (
              <Card
                key={item.id}
                onClick={() => GenerateToken(item.id, item.form_slug)}
                className="relative flex flex-col items-center justify-center w-full py-6 bg-[#EDEDED] hover:cursor-pointer"
              >
                <CardContent className="flex flex-col items-center justify-center text-center">
                  <img
                    src={
                      item.logo ? `${VITE_VIEW_S3_URL + item.logo}` : defaultImg
                    }
                    alt={item.name}
                    className="w-[5rem] h-[5rem] object-contain"
                  />
                </CardContent>
                <CardFooter className="flex items-center justify-center text-center">
                  <div className="text-center px-2">
                    <p className="capitalize text-[#767676] hover:text-black leading-1 m-0  p-0 font-semibold text-[1.2rem]">
                      {item.name}
                    </p>
                  </div>
                </CardFooter>
              </Card>
            ))
          ) : (
            <div className="col-span-1 md:col-span-2 lg:col-span-4 flex items-center justify-center h-full">
              {renderNoServiceMessage()}
            </div>
          )}
        </section>

        {/* Pagination Controls */}
        <div className="flex items-center justify-end gap-4 my-4 w-full">
          {searchCriteria.offset !== 0 && (
            <Button
              variant="solid"
              size="icon"
              onClick={handlePrevPage}
              disabled={searchCriteria.offset === 0}
              className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-7 w-7" />
            </Button>
          )}
          <Button
            variant="solid"
            size="icon"
            onClick={handleNextPage}
            disabled={isLastPage}
            className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-7 w-7" />
          </Button>
        </div>

        <Separator
          orientation="horizontal"
          className="h-[.1rem] w-[100%] bg-gray-300"
        />

        {/* Footer */}
        <div className="flex items-center justify-between my-4">
          <div>
            {/* <p className="text-[#767676] font-normal">{`${time} of ${date}`}</p> */}
          </div>
          <div className="bg-gray-300 text-gray-500 p-2 rounded-full">
            <p className="text-[.9rem]">Information & Contact</p>
          </div>
        </div>
      </div>

      {/* Custom Alert Dialog */}
      <CustomAlertDialog
        body={renderBody}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        isBodyAllow={true}
        title="Service Details"
        description="More details about the selected service"
        onConfirm={() => { }}
        className="w-[20rem] bg-slate-600"
      />
    </main>
  );
};

export default OfferService;
